/* @flow */
import idx from 'idx';
import * as React from 'react';
import { graphql, compose } from 'react-apollo';
import { once } from 'lodash';
import invariant from 'invariant';

import adminSchoolSisDashboardQuery from 'admin/queries/adminSchoolSisDashboardQuery.gql';
import AdminSisSyncConnectedView from 'admin/components/AdminSisSyncConnectedView';
import AdminSisSyncHoldView from 'admin/components/AdminSisSyncHoldView';
import AdminSisSyncProcessingView from 'admin/components/AdminSisSyncProcessingView';
import AdminSisSyncSchoolNotSetupView from 'admin/components/AdminSisSyncSchoolNotSetupView';
import LoadingIndicator from 'components/LoadingIndicator.jsx';
import awaitData from 'lib/awaitData';
import EventTracking from 'lib/EventTracking';
import LinkConstants from 'constants/LinkConstants';
import Link from 'components/Link.jsx';

export type IssueSummary = {
  severity: string,
  affectedCount: number,
  affectedType: string,
  description: string,
  resolution: string,
  downloadUrl: ?string,
};

export type AffectedResource = {
  count: number,
  description: string,
  downloadUrl: string,
};

export type HoldSummary = {
  hold: string,
  permanentlyIgnoreable: boolean,
  temporarilyIgnoreable: boolean,
  affectedResourcesSummary: Array<AffectedResource>,
};

export type SisSyncOverview = {
  parentCount: number,
  studentCount: number,
  teacherCount: number,
  errorCount: number,
  alertCount: number,
  syncStatus: string,
  syncMethod: string,
  lastSyncTimestamp: number,
  allIssuesDownloadUrl: ?string,
  issueSummaries: Array<IssueSummary>,
  holdSummary: ?HoldSummary,
  canNotifyTeachers: boolean,
  canNotifyStudents: boolean,
  canNotifyParents: boolean,
};

type Organization = {
  id: number,
  uuid: string,
  name: string,
  sisSyncOverview: SisSyncOverview,
  class: Object,
};

type Props = {
  orgId: number,
  organization: Organization,
};

class AdminSchoolSisDashboard extends React.Component<Props> {
  componentWillMount() {
    this.trackView();
  }

  trackView = once(() => {
    EventTracking.view('rostering.sis_dash.viewed', {
      organization_uud: this.props.organization.uuid,
    });
  });

  render() {
    const { organization } = this.props;
    const { sisSyncOverview } = organization;

    switch (sisSyncOverview.syncStatus.toLowerCase()) {
      case 'not_set_up':
        return <AdminSisSyncSchoolNotSetupView />;
      case 'on_hold':
        invariant(sisSyncOverview.holdSummary, 'syncStatus is on_hold but holdSummary is null');
        return (
          <AdminSisSyncHoldView
            orgUuid={organization.uuid}
            holdSummary={sisSyncOverview.holdSummary}
            lastSyncTimestamp={sisSyncOverview.lastSyncTimestamp}
            syncMethod={sisSyncOverview.syncMethod}
          />
        );
      case 'connected':
        return (
          <AdminSisSyncConnectedView
            orgUuid={organization.uuid}
            orgId={organization.id}
            sisSyncOverview={sisSyncOverview}
            className="AdminSchoolSisDashboard"
          />
        );
      case 'processing':
        return (
          <AdminSisSyncProcessingView
            orgUuid={organization.uuid}
            status="processing"
            subtext={<span>We’re still processing your data. We’ll be done soon!</span>}
          />
        );
      case 'pending':
        return (
          <AdminSisSyncProcessingView
            orgUuid={organization.uuid}
            status="pending"
            subtext={
              <span>
                We’re experiencing slow syncing. Please wait while it finishes. If the issue
                persists,
                <Link href={LinkConstants.CONTACT_FORM} target="_blank">
                  {' '}
                  contact support
                </Link>
                .
              </span>
            }
          />
        );
      default:
        console.error('AdminSchoolSisDashboard - syncStatus not recognized');
        return <span>Uh, oh. You should not be here, something weird happened.</span>;
    }
  }
}

const withOrganization = graphql(adminSchoolSisDashboardQuery, {
  options: ({ orgId }) => ({
    variables: {
      organizationId: [orgId],
    },
  }),
  props: ({ data }) => ({
    organization: idx(data, _ => _.organizations[0]),
  }),
});

export { AdminSchoolSisDashboard };
export default compose(
  withOrganization,
  awaitData(['organization'], <LoadingIndicator height={400} size="small" />)
)(AdminSchoolSisDashboard);
